import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Leaf, Maximize2, Minimize2, Minus, Plus, RotateCcw, X } from "lucide-react";
import { PageHead } from "@/components/Motion";
import { Kept, Loading, useApi } from "@/components/Common";
import { media } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const short = (n = "") => {
  const q = n.match(/"([^"]+)"/);
  const parts = n.replace(/"[^"]*"/, "").replace(/\([^)]*\)/g, "").trim().split(/\s+/);
  const pre = parts.length > 2 && /^(de|van|du|le)$/i.test(parts[parts.length - 2]) ? `${parts[parts.length - 2]} ` : "";
  const last = `${pre}${parts[parts.length - 1]}`;
  if (q && q[1].length <= 6 && !q[1].includes("\u2026")) return `${q[1]} ${last}`;
  return parts.length > 1 ? `${parts[0]} ${last}` : n;
};

const layout = (members, gap = 150) => {
  const gens = {};
  members.forEach((m) => { const g = m.generation || 1; (gens[g] = gens[g] || []).push(m); });
  const keys = Object.keys(gens).map(Number).sort((a, b) => a - b);
  const W = Math.max(1100, ...keys.map((g) => gens[g].length * 175));
  const pos = {};
  keys.forEach((g, gi) => {
    const row = gens[g];
    row.forEach((m, i) => { pos[m.key] = { x: ((i + 1) * W) / (row.length + 1), y: 90 + gi * gap, m }; });
  });
  return { pos, h: Math.max(460, 90 + keys.length * gap), w: W };
};

const z0 = () => (typeof window !== "undefined" && window.innerWidth < 640 ? 2.2 : 1);

export default function Tree() {
  const { session } = useSite() || {};
  const { data, loading } = useApi("/public/tree", [session?.active]);
  const members = Array.isArray(data?.members) ? data.members : [];
  const [full, setFull] = useState(false);
  const { pos, h, w } = useMemo(() => layout(members, full ? 115 : 150), [members, full]);
  const [z, setZ] = useState(z0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [sel, setSel] = useState(null);
  const drag = useRef(null);
  const nodes = Object.values(pos);
  const R = full ? 34 : 26;
  const toggleFull = () => { setZ(full ? z0() : 1); setFull((f) => !f); setPan({ x: 0, y: 0 }); };
  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { setFull(false); setZ(z0()); setPan({ x: 0, y: 0 }); } };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [full]);
  return (
    <div data-testid="tree-page">
      <PageHead eyebrow="The Family Tree" title="Every leaf, in its place" line="Every generation of our family, from 1756 to today. Children are always private." />
      <section className="max-w-7xl mx-auto px-4 md:px-10 pb-28">
        {loading ? <Loading /> : !nodes.length ? <Kept title="The tree is being planted" text="Family members will appear here shortly." /> : (
          <div className={`${full ? "fixed inset-0 z-[58] rounded-none" : "relative rounded-2xl border h-[78vh] min-h-[520px]"} border-[#C9A24B]/30 bg-[radial-gradient(ellipse_at_top,#163B29,#0B1D14_70%)] overflow-hidden w-full touch-none`}
            onWheel={(e) => setZ((v) => Math.min(2.5, Math.max(0.5, v - e.deltaY * 0.001)))}
            onPointerDown={(e) => { drag.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }; }}
            onPointerMove={(e) => { if (drag.current) setPan({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); }}
            onPointerUp={() => { drag.current = null; }} onPointerLeave={() => { drag.current = null; }} data-lenis-prevent data-testid="tree-canvas">
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <button className="lb-btn" onClick={() => setZ((v) => Math.min(2.5, v + 0.2))} data-testid="tree-zoom-in" aria-label="Zoom in"><Plus size={16} /></button>
              <button className="lb-btn" onClick={() => setZ((v) => Math.max(0.5, v - 0.2))} data-testid="tree-zoom-out" aria-label="Zoom out"><Minus size={16} /></button>
              <button className="lb-btn" onClick={() => { setZ(1); setPan({ x: 0, y: 0 }); }} data-testid="tree-reset" aria-label="Reset"><RotateCcw size={16} /></button>
              <button className="lb-btn" onClick={toggleFull} data-testid="tree-fullscreen-toggle" aria-label={full ? "Exit full screen" : "Full screen"}>{full ? <Minimize2 size={16} /> : <Maximize2 size={16} />}</button>
            </div>
            {full && <p className="absolute top-5 left-4 md:left-8 z-10 font-display text-xl md:text-2xl text-[#F6EFE0]" data-testid="tree-fullscreen-title">The Family Tree <span className="hidden md:inline font-mono-x text-[11px] tracking-[0.2em] text-[#C9A24B] ml-3">ESC TO CLOSE</span></p>}
            <p className="absolute bottom-4 left-4 z-10 font-mono-x text-[11px] tracking-[0.2em] text-[#C9A24B]">DRAG TO MOVE · SCROLL OR BUTTONS TO ZOOM</p>
            <svg viewBox={`0 0 ${w} ${h}`} className={`w-full h-full ${full ? "pt-16 pb-10" : ""}`} style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${z})`, transition: drag.current ? "none" : "transform .4s cubic-bezier(.22,1,.36,1)" }}>
              {nodes.flatMap((n) => (n.m.parent_keys || []).filter((k) => pos[k]).map((k) => (
                <motion.path key={`${k}-${n.m.key}`} d={`M${pos[k].x} ${pos[k].y} C ${pos[k].x} ${(pos[k].y + n.y) / 2}, ${n.x} ${(pos[k].y + n.y) / 2}, ${n.x} ${n.y}`} stroke="#C9A24B" strokeOpacity=".45" strokeWidth="1.5" fill="none"
                  initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.4, delay: 0.3 }} />
              )))}
              {nodes.map((n, i) => (
                <motion.g key={n.m.key} initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 + i * 0.06 }} style={{ transformOrigin: `${n.x}px ${n.y}px`, transformBox: "view-box" }}
                  className="cursor-pointer" onClick={() => setSel(n.m)} data-testid={`tree-node-${i}`}>
                  <circle cx={n.x} cy={n.y} r={R} fill="#0F2A1D" stroke="#C9A24B" strokeWidth="1.2" />
                  {n.m.anon || !n.m.photo_url ? (
                    <path d={`M${n.x - 9} ${n.y + 2} c 6 -14 16 -14 20 -4 c -4 10 -14 12 -20 4 z`} fill="#C9A24B" />
                  ) : (
                    <>
                      <clipPath id={`c${i}`}><circle cx={n.x} cy={n.y} r={R - 2} /></clipPath>
                      <image href={media(n.m.photo_url)} x={n.x - R + 2} y={n.y - R + 2} width={2 * R - 4} height={2 * R - 4} clipPath={`url(#c${i})`} preserveAspectRatio="xMidYMid slice" style={{ filter: "grayscale(1)" }} />
                    </>
                  )}
                  <text x={n.x} y={n.y + R + 20} textAnchor="middle" fill="#F6EFE0" fontSize={full ? 15 : 13} fontFamily="Playfair Display">{n.m.anon ? "Family member" : short(n.m.name)}</text>
                  {!n.m.anon && n.m.born && <text x={n.x} y={n.y + R + 37} textAnchor="middle" fill="#E4C87F" fontSize={full ? 12 : 11}>{n.m.born}{n.m.died ? `–${n.m.died}` : ""}</text>}
                </motion.g>
              ))}
            </svg>
          </div>
        )}
        <AnimatePresence>
          {sel && (
            <motion.div className="fixed inset-x-4 bottom-4 md:inset-x-auto md:right-8 md:bottom-8 md:w-96 z-[60] glass rounded-2xl p-6" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }} data-testid="tree-member-card">
              <button className="absolute top-3 right-3 lb-btn !w-8 !h-8" onClick={() => setSel(null)} data-testid="tree-card-close"><X size={14} /></button>
              <p className="font-mono-x text-[11px] tracking-[0.25em] text-[#C9A24B]">GENERATION {sel.generation} · {sel.branch}</p>
              {sel.anon ? (
                <div className="mt-4 flex items-center gap-3"><Leaf className="text-[#C9A24B]" /><p className="font-accent italic text-xl">Family member, details are private</p></div>
              ) : (
                <>
                  {sel.photo_url && <img src={media(sel.photo_url)} alt={sel.name} className="mt-4 w-full h-40 object-cover rounded-lg border border-[#C9A24B]/40" />}
                  <h3 className="mt-4 font-display text-2xl">{sel.name}</h3>
                  <p className="text-sm text-[#E4C87F]">{sel.born}{sel.died ? ` – ${sel.died}` : ""}</p>
                  <p className="mt-3 text-sm text-[#F6EFE0]/85">{sel.bio}</p>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}
